'use client';

import { useTransition } from 'react';
import type { ReactNode } from 'react';

import { ResponsiveDialog } from '@ui/components/common/dialog/responsiveDialog';
import { Button } from '@ui/components/ui/button';

export interface ConfirmDialogProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;
  /**
   * Callback when the modal should be closed
   */
  onClose: () => void;
  /**
   * Callback when user chooses to become a mentor
   */
  onConfirm: () => Promise<void> | void;
  /**
   * Callback when user chooses to stay a learner
   */
  onReject?: () => void;

  title?: string;
  description?: string;
  /**
   * Optional extra content rendered between description and actions
   */
  additionalChildren?: ReactNode;
  rejectButtonText?: string;
  confirmButtonText?: string;
  icon?: ReactNode;
  size?: 'sm' | 'lg' | 'default' | 'xl';
}

/**
 * Modal for asking user if they want to become a mentor
 * Shows confirmation dialog with illustration and action buttons
 */

export const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  onReject,
  title = 'Confirm',
  description = 'Are you sure you want to proceed?',
  additionalChildren,
  rejectButtonText = 'Cancel',
  confirmButtonText = 'Confirm',
  icon,
  size
}: ConfirmDialogProps) => {
  const handleReject = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    onReject?.();
    onClose();
  };

  const [isPending, startTransition] = useTransition();

  const handleConfirm = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    startTransition(async () => {
      await onConfirm();
      onClose();
    });
  };

  return (
    <ResponsiveDialog isOpen={isOpen} onClose={onClose} showCloseButton={false} size={size} className="text-center">
      {/* Illustration */}
      <div className="flex justify-center mb-2">{icon}</div>

      {/* Title */}
      <h2 className="text-xl font-semibold text-gray-900">{title}</h2>

      {/* Description */}
      <p className="text-neutral-700 mb-3 text-base leading-relaxed">{description}</p>

      {/* Additional content area */}
      {additionalChildren && <div className="mb-3 text-left">{additionalChildren}</div>}

      {/* Action Buttons */}
      <div className="flex flex-row gap-2">
        {/* No, Stay a Learner Button */}
        <Button
          onClick={handleReject}
          variant="ghost"
          className="text-orange-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg py-3  text-base font-medium flex-1"
          size="lg"
          type="button"
        >
          {rejectButtonText}
        </Button>
        {/* Yes Button */}
        <Button
          onClick={handleConfirm}
          className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg py-3 font-medium  text-base flex-1"
          size="lg"
          type="button"
          disabled={isPending}
        >
          {confirmButtonText}
        </Button>
      </div>
    </ResponsiveDialog>
  );
};
